import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Map, Package, Tag, Brain, Settings, ChevronLeft, ChevronRight, BarChart3, ClipboardList, Globe, GitCompare, ScrollText, Users, ShoppingBag, Footprints, Store, BookOpen, CalendarDays } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const navItems = [
  { path: '/', label: 'Overview', icon: LayoutDashboard, module: 'overview' },
  { path: '/alerts', label: 'Beat Briefings', icon: ClipboardList, module: 'alerts' },
  { path: '/performance', label: 'Performance', icon: BarChart3, module: 'performance' },
  { path: '/territory', label: 'Territory', icon: Map, module: 'territory' },
  { path: '/map', label: 'Map View', icon: Globe, module: 'map' },
  { path: '/comparison', label: 'Comparison', icon: GitCompare, module: 'comparison' },
  { path: '/distribution', label: 'Distribution', icon: Package, module: 'distribution' },
  { path: '/products', label: 'Products', icon: ShoppingBag, module: 'products' },
  { path: '/visits', label: 'Visits', icon: Footprints, module: 'visits' },
  { path: '/outlets', label: 'Outlets', icon: Store, module: 'outlets' },
  { path: '/beat-plans', label: 'Beat Plans', icon: CalendarDays, module: 'beat-plans' },
  { path: '/schemes', label: 'Schemes', icon: Tag, module: 'schemes' },
  { path: '/ai-insights', label: 'AI Insights', icon: Brain, module: 'ai-insights' },
  { path: '/users', label: 'Users', icon: Users, module: 'users' },
  { path: '/audit-log', label: 'Audit Log', icon: ScrollText, module: 'audit-log' },
  { path: '/guide', label: 'User Guide', icon: BookOpen, module: null },
  { path: '/configuration', label: 'Configuration', icon: Settings, module: 'configuration' },
];

export default function Sidebar({ open, onToggle }) {
  const location = useLocation();
  const { hasModule } = useAuth();

  const visibleItems = navItems.filter(item => item.module === null || hasModule(item.module));

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-card border-r border-border/50 flex flex-col z-40 transition-[width] duration-200 ease-out ${open ? 'w-60' : 'w-16'}`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-3 border-b border-border/50 gap-3">
        <img src="/keya-logo.png" alt="Keya" className="w-10 h-10 object-contain rounded" />
        {open && (
          <div className="flex flex-col">
            <span className="font-heading font-bold text-sm tracking-tight text-foreground">KEYA PULSE <span className="text-[10px] font-normal text-muted-foreground">v1.0</span></span>
            <span className="text-[10px] text-muted-foreground tracking-widest uppercase">Analytics</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
        {visibleItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
          return (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              <Icon size={18} strokeWidth={1.5} className="flex-shrink-0" />
              {open && <span className="truncate">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        data-testid="sidebar-toggle"
        className="h-10 flex items-center justify-center border-t border-border/50 text-muted-foreground hover:text-foreground transition-colors duration-150"
      >
        {open ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
      </button>
    </aside>
  );
}
